'use client'

import { MouseEvent, useMemo, useState } from 'react'

import { faUserPlus, faCheck } from '@document-crunch/pro-regular-svg-icons'

import {
  Avatar,
  Icon,
  IconColor,
  Spinner,
} from '@dc-shared-web/components/base'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@dc-shared-web/components/shadcn'
import {
  AssignmentUser,
  useCreateAssignment,
  useDeleteAssignment,
  useGetAssignmentUsers,
  useGetAssignments,
  useUpdateAssignment,
} from '@dc-shared-web/hooks/api/assignment'
import { cn } from '@dc-shared-web/lib'
import {
  Tooltip,
  TooltipTrigger,
  TooltipArrow,
  TooltipProvider,
  TooltipContent,
} from '@dc-shared-web/components/shadcn/tooltip'

const formatUsername = (user: AssignmentUser) =>
  `${user.firstname} ${user.lastname}`

const getTooltipText = (
  assignedUser: AssignmentUser | undefined,
  disabled: boolean
) => {
  if (assignedUser) {
    return `Assigned to ${formatUsername(assignedUser)}`
  }

  return disabled ? 'No user assigned' : 'Assign user'
}

export default function AssignmentSelector({
  type,
  crunchUuid,
  checklistDataUuid,
  sectionRefId,
  disabled = false,
  className,
}: {
  type: 'checklist' | 'playbook'
  crunchUuid: string
  checklistDataUuid: string
  sectionRefId?: string
  disabled?: boolean
  className?: string
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)

  const { data: assignments, isLoading: isLoadingAssignments } =
    useGetAssignments({
      checklist_data_uuid: checklistDataUuid,
      crunch_uuid: crunchUuid,
    })
  const { data: assignmentUsers, isLoading: isLoadingAssignmentUsers } =
    useGetAssignmentUsers(crunchUuid)
  const { mutate: deleteAssignment } = useDeleteAssignment()
  const { mutate: updateAssignment } = useUpdateAssignment()
  const { mutate: createAssignment } = useCreateAssignment()

  const assignment = useMemo(
    () =>
      assignments?.find((assignment) =>
        sectionRefId
          ? assignment.section_ref_id === sectionRefId
          : !assignment.section_ref_id
      ),
    [assignments, sectionRefId]
  )
  const assignedUser = useMemo(
    () =>
      assignmentUsers?.find((user) => user.user_uuid === assignment?.user_uuid),
    [assignment, assignmentUsers]
  )
  const sortedAssignmentUsers = useMemo(
    () =>
      assignmentUsers?.sort((a, b) =>
        `${a.firstname} ${a.lastname}`
          .toLowerCase()
          .localeCompare(`${b.firstname} ${b.lastname}`.toLowerCase())
      ),
    [assignmentUsers]
  )

  const onTriggerClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setDropdownOpen(!dropdownOpen)
  }

  const onAssignUser = (userUuid: string) => {
    if (assignment) {
      updateAssignment({
        assignment_uuid: assignment.uuid,
        checklist_data_uuid: checklistDataUuid,
        user_uuid: userUuid,
        crunch_uuid: crunchUuid,
        section_ref_id: sectionRefId,
      })
    } else {
      createAssignment({
        checklist_data_uuid: checklistDataUuid,
        user_uuid: userUuid,
        crunch_uuid: crunchUuid,
        section_ref_id: sectionRefId,
      })
    }
    setDropdownOpen(false)
    setTooltipOpen(false)
  }

  const onUnassignUser = () => {
    if (assignment) {
      deleteAssignment({
        assignment_uuid: assignment.uuid,
        checklist_data_uuid: checklistDataUuid,
        crunch_uuid: crunchUuid,
      })
    }
    setDropdownOpen(false)
    setTooltipOpen(false)
  }

  // Ideally we could reuse the default filter from cmdk but our current version (0.2.1)
  // does not export it. We would need to upgrade but this has many impacts as it would
  // require upgrading shadcn and there is a high risk of breaking changes.
  const onFilter = (value: string, search: string) => {
    // We appended the user_uuid to the value to avoid breaking keyboard navigation
    // so we need to split it to get the name we want to filter on.
    const [name] = value.split('|')
    return name.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
  }

  if (isLoadingAssignments) {
    return (
      <div
        className={cn(
          'tw-rounded-full tw-px-0 tw-py-0 tw-h-8 tw-w-8 tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-grow-0 tw-bg-emerald-100 tw-animate-skeleton-shimmer',
          className
        )}
      />
    )
  }

  return (
    <TooltipProvider>
      <Tooltip open={tooltipOpen} delayDuration={150}>
        <TooltipTrigger>
          <Popover open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <PopoverTrigger disabled={disabled}>
              <div
                onMouseEnter={() => setTooltipOpen(true)}
                onMouseLeave={() => setTooltipOpen(false)}
              >
                <button
                  onClick={onTriggerClick}
                  disabled={disabled}
                  className='tw-font-normal'
                >
                  {assignedUser ? (
                    <Avatar
                      fallback={`${assignedUser.firstname.charAt(0)}${assignedUser.lastname.charAt(0)}`}
                      size='sm'
                      fallbackColor='blue'
                    />
                  ) : (
                    <div className='tw-rounded-full tw-px-0 tw-py-0 tw-h-8 tw-w-8 tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-grow-0 tw-border tw-border-gray-200 tw-border-dashed tw-bg-white hover:tw-bg-gray-50'>
                      <Icon
                        icon={faUserPlus}
                        className='tw-h-4 tw-w-4'
                        color={IconColor.iconMuted}
                      />
                    </div>
                  )}
                </button>
              </div>
            </PopoverTrigger>
            <PopoverContent
              align='end'
              className='tw-w-64 tw-rounded-xl tw-p-0 tw-border-gray-200 tw-max-h-56 tw-flex tw-flex-col'
            >
              <Command
                className='tw-bg-transparent'
                filter={onFilter}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
              >
                <CommandInput
                  placeholder={`Assign to ${type}...`}
                  disabled={isLoadingAssignmentUsers}
                />
                <CommandList>
                  <CommandEmpty>
                    {isLoadingAssignmentUsers ? <Spinner /> : 'No results'}
                  </CommandEmpty>
                  <CommandGroup className='tw-p-2'>
                    {sortedAssignmentUsers &&
                      sortedAssignmentUsers.length > 0 &&
                      assignedUser && (
                        <CommandItem
                          onSelect={onUnassignUser}
                          className='tw-cursor-pointer tw-rounded-lg data-[selected]:tw-bg-gray-100 tw-text-sm tw-p-2 tw-flex tw-gap-2 tw-group tw-items-center'
                        >
                          <div className='tw-rounded-full tw-bg-gray-100 tw-p-1 tw-border tw-border-gray-200 tw-border-dashed group-data-[selected]:tw-border-0 tw-bg-white tw-h-8 tw-w-8 tw-flex tw-items-center tw-justify-center'>
                            <Icon icon={faUserPlus} className='tw-h-4 tw-w-4' />
                          </div>
                          <span>Unassigned</span>
                        </CommandItem>
                      )}
                    {sortedAssignmentUsers?.map((user) => (
                      <CommandItem
                        key={user.user_uuid}
                        value={
                          /* We need to add the user_uuid otherwise keyboard navigation breaks for users with same name but different uuid */
                          `${formatUsername(user)}|${user.user_uuid}`
                        }
                        onSelect={() => onAssignUser(user.user_uuid)}
                        className='tw-cursor-pointer tw-rounded-lg data-[selected]:tw-bg-gray-100 tw-text-sm tw-p-2 tw-flex tw-gap-2 tw-group tw-items-center'
                      >
                        <Avatar
                          fallback={`${user.firstname.charAt(0)}${user.lastname.charAt(0)}`}
                          size='sm'
                          fallbackColor='blue'
                        />
                        <span className='tw-flex-grow tw-truncate'>
                          {formatUsername(user)}
                        </span>
                        {assignedUser &&
                          assignedUser.user_uuid === user.user_uuid && (
                            <Icon icon={faCheck} color={IconColor.tertiary} />
                          )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </TooltipTrigger>
        <TooltipContent side='top'>
          <TooltipArrow />
          <span className='tw-font-normal'>
            {getTooltipText(assignedUser, disabled)}
          </span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
